import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'

import EmptyList from 'components/EmptyList'
import { SortablePagingParams } from 'components/Table'
import TableLoader from 'components/Table/Loader'
import { HeadCell } from 'components/Table/types'

interface Props<T> {
  data: T[]
  isLoading: boolean
  pagingParams?: SortablePagingParams<T>
  onRowClick?: (row: T) => void
  headCells: HeadCell<T>[]
  gridTemplateColumns: string
}

const MIN_ROW_HEIGHT = 58
const DataTableBody = <T,>({
  data,
  isLoading,
  onRowClick,
  pagingParams,
  headCells,
  gridTemplateColumns,
}: Props<T>) => {
  const minHeight = MIN_ROW_HEIGHT * (pagingParams?.pageSize || 5)

  return (
    <TableBody
      sx={{
        display: 'block',
        height: minHeight,
        overflow: 'auto',
      }}
    >
      {isLoading ? (
        <TableLoader />
      ) : !data.length ? (
        <EmptyList height={minHeight} />
      ) : (
        data.map((item, index) => (
          <TableRow
            onClick={() => onRowClick?.(item)}
            key={index}
            sx={{
              display: 'grid',
              gridTemplateColumns: gridTemplateColumns,
              alignItems: 'center',
              minHeight: `${MIN_ROW_HEIGHT}px`,
              '&:nth-of-type(odd)': {
                backgroundColor: (theme) => theme.palette.gray.light,
              },
              cursor: onRowClick ? 'pointer' : undefined,
              ':hover': {
                bgcolor: onRowClick ? 'primary.light' : undefined,
              },
            }}
          >
            {headCells.map((cell) => {
              const { align = 'center' } = cell

              return (
                <TableCell key={cell.id} align={align} sx={{ justifyContent: align }}>
                  {cell.renderValue ? cell.renderValue(item) : String(item[cell.id])}
                </TableCell>
              )
            })}
          </TableRow>
        ))
      )}
    </TableBody>
  )
}

export default DataTableBody
